/**
 * 配菜组「至少要选几样 / 最多能选几样」的唯一定义 /
 * The one definition of a section's pick bounds.
 *
 * 这条语义在五个前端仓里逐字一致，所以单独成文件：谁要判断"这组还能不能再选"、
 * "这组选够了没有"，都从这里取数 —— 预勾选截断、置灰、换选、数量步进、单选渲染、
 * 以及六道下限闸门，一个都不许自己解析 `maxOption` / `minOption`。
 * （本文件不 import 任何东西，所以谁都能引它，不会绕出循环依赖。）
 *
 *   parseUsable(value):                 ← 「这一栏里有没有一个能用的数」
 *     if typeof value 不是 number 也不是 string: null
 *     n = parseInt(value)               ← 截断取整，见下方「为什么是 parseInt」
 *     if n 不是有限数 or n <= 0: null
 *     else: n
 *
 *   sectionBounds(group):               ← 上下限一起算出来，唯一实现（§8，五仓逐字一致）
 *     ① 按 limitType 分派
 *          'range'  → lo = parseUsable(minOption)，hi = parseUsable(maxOption)
 *          'limit'  → lo = 没配（那一栏在这种组里可能是被镜像写进去的上限）
 *                     hi = parseUsable(maxOption)
 *          其余      → 'exactly'，**以及任何认不出来的值**（没写 / 空串 / 拼错）：
 *                     n = lo ?? hi ?? (必选组 ? 1 : NO_CAP)，然后 lo = hi = n
 *     ② 必选组的地板恒为 1（一个字段都不读）
 *          if 必选组 且 lo 没配: lo = 1
 *     ②½ 下限不能超过实际选得到的数量（§8.6，§8.13 修正）
 *          if lo > 选项条数:
 *              lo = 选项条数；**exactly 组的 hi 跟着一起降**（两头是同一个数）
 *              必选组再 lo = max(lo, 1)（exactly 的 hi 同样一起抬），并 warn 一次
 *     ③ 坏配置兜底 —— **只在算出来做不到时进入**（lo 与 hi 都有数且 lo > hi）
 *          lo = hi                      ← 塌成「正好 hi 样」，并 warn 一次
 *          （§8.1 原文那三个算子全是死操作，已删，理由见 ③ 那一段）
 *
 *   sectionMaxOption(group) = sectionBounds(group).upper   ← 上限：最多能选几样
 *   sectionLowerBound(group) = sectionBounds(group).lower  ← 下限：至少要选几样
 *
 *   lowerBound(group):                  ← 跨仓契约本体：后台**在那一栏里写了什么**
 *     parseUsable(minOption)            ← null = 这一栏没配。**不看 limitType**（见下）
 *
 * ── `exactly` 为什么以 minOption 为主（owner 2026-08-08 裁定）──────────────
 *
 * 裁定原文：**「如果是 exactly，那么 min 的数量为主，max 无视，除非 min 没有数量。」**
 *
 * 这条**推翻了更早的一版**（那一版是「max 优先，max 不可用才回落 min」）。
 * 翻转的理由不是口味，是本仓的校验侧从来就只认 min：`getCheckNum` 与
 * `validateAndUpdateMod` 的 `exactly` 分支比对的都是那个数（三个渲染文件各一份）。
 * 于是「max 优先」会造出一个自相矛盾的组：`exactly min=1 max=2` 时，截断 / 置灰 /
 * 数量步进按 2 放行（顾客能勾两样、预勾选也能预勾两样），校验却只认 1 ——
 * 组一打开就是红的，顾客照着界面允许的操作走永远过不了，而且看不出哪里错。
 * 以 min 为主之后这条自动消失：上限与校验目标是同一个数。
 * （现在校验侧直接向本文件要这个数，见下一节。）
 *
 * ── `exactly` 两个键都空时：只有必选组兜底 1（owner 2026-08-08 第四次裁定）──
 *
 * 裁定原文：**「optional 和 exactly 都空，那就是可选组，可选任意选项，
 * 相当于 max=0、min=0 的可选组。」**
 *
 * 这条**修正了同日第三次裁定**（那一版是「什么都没有就是 1」，不分必选可选）。
 * 兜底 1 的正当性只有一条，而且只对必选组成立：
 *
 *  - **必选组**：校验侧要拿这个数去比（`getCheckNum` / `validateAndUpdateMod` 的
 *    `exactly` 分支，三个渲染文件各一份）。说不出数就等于拿 `parseInt('')` 的 NaN
 *    去比，NaN 与任何数都不相等 → 这一组**永远判红、商品永远加不进购物车**，
 *    顾客怎么点都没用。给它一个确定的 1 是**修复**：组头念「Select 1」、画成单选、
 *    勾一个就过。
 *  - **可选组**（以及压根没写 `type` 的组）：本仓三条校验路（`getCheckNum` /
 *    `validateAndUpdateMod` / `_validateRequiredModifications`）全都只在
 *    `type === 'required'` 里跑，这些组**根本不做校验**。所以对它兜底 1 修不了
 *    任何东西，只会把「可选任意个」收窄成「只能选一样」—— 那是把一个没坏的组弄坏。
 *    说不出数就是 NO_CAP，与「没配上限的可选组」同一条路：不截断预勾选、不置灰、
 *    不画单选、组头不念数字。
 *    所以这条分岔问的是「是不是必选」而不是「是不是可选」：没写 type 的组不做校验，
 *    跟可选组走同一边。
 *
 * 于是 `exactly` 的组**不再永远给得出一个数**：必选组永远给得出，可选组会是 NO_CAP。
 * 下游凡是「exactly 一定有数」的假设都得看清自己那一支有没有被 `required` 挡着。
 *
 * 「可用」= `parseInt` 之后的正整数。小数**截断取整**（`'1.5'` → 1）；负数 / 零 /
 * 布尔 / 非数字串一律算**没写**，没写就继续往下找（min 不可用看 max，max 也不可用
 * 才轮到上面那条分岔）。
 *
 * ── 为什么是 parseInt（owner 2026-08-10 裁定 R3 / R4，契约 v5）────────────
 *
 * 裁定原文：**「maxOption 都不允许小数，凡是小数都得 parseInt」**、
 * **「下限同样 parseInt，负数/读不出当没配」**。
 *
 * 这**推翻了 v4**（那一版用 `Number()` + `isInteger`，于是 `'1.5'` 被判成「没写」）。
 * 换成 parseInt 白送一个一致性：本仓那六道下单闸门本来写的就是
 * `parseInt(selectedNum) < parseInt(minOption)`，下限契约采用 parseInt 语义之后，
 * **契约和闸门是同一个数**，不是碰巧一致 —— 闸门现在直接调 `lowerBound`。
 * 若改用「`Number` 之后 floor」，`'1e3'` 契约得 1000、闸门得 1，第一天就分叉。
 *
 * 刻意**不写 radix**：闸门那六处历史上就是 `parseInt(minOption)`。写成
 * `parseInt(x, 10)` 会在 `'0x10'` 上与它们分岔（16 vs 0）。
 *
 * `'3abc'` / `'1e3'` / `'0x10'` / 309 位数字串这些形状**不是设计输入**
 * （owner 2026-08-10：「这类情况一旦出现，手动排查解决原生数值才对」）。
 * 契约怎么落它们就是怎么落，**不为它们加分支、加常量、加兜底**。
 *
 * On an `exactly` section the number lives in `minOption`: that is the field every
 * validation path in this repo compares against, so the cap must agree with it or
 * the section opens already-invalid. `maxOption` is only consulted when `minOption`
 * carries no usable quantity. When neither field carries one the answer depends on
 * the section type: a **required** section falls back to 1 (validation compares
 * against this number, and the old NaN comparison left such sections permanently
 * un-passable), while an **optional** section reports NO_CAP — nothing validates it,
 * so capping it at 1 would only turn "pick as many as you like" into "pick one".
 *
 * 非 `exactly` 的组一个字都没改：上限永远只读 `maxOption`，解析不出来就是 NO_CAP。
 * 那边「没配上限」= 不限制，是合法语义，没有兜底可言；而 `minOption` 在那些组里
 * 是**下限**，拿它当上限会把「至少 1」翻译成「最多 1」。
 *
 * ── 必选组的地板是 1（owner 2026-08-10 裁定）─────────────────────────────
 *
 * 裁定原文：**「必选组至少选 1 个是刚需，必选组不存在连 1 个都不选就能过去的，
 * 这是真实逻辑规则，这个不能妥协。」**
 *
 * 落地之前本仓真实的行为是：`required` + `range` + `minOption` 说不出数
 * （没写 / `''` / `'0'`）时，六道闸门里那句 `minNum !== null && …` **整条短路**，
 * 一样都不选照样过。而且不用什么刁钻操作 —— 勾一下再取消就行：那一下会把这组
 * 推进 `passList`，加购时的 `_validateRequiredModifications` 只看
 * 「在不在 `passList` 里」，于是这组连红字都不会有。屏上念的却是
 * 「Select between 1 and 3 (Required)」（`sectionDesc` 的 `?? 1`）——
 * **屏幕说一套、闸门做另一套**，而顾客只看得见屏幕那一套。
 *
 * 所以下限从此分两个量，别再混用：
 *
 *  - `lowerBound`        = 后台**配了**什么（原始值，可能是 null）。契约不动，
 *                          五仓逐字一致，它是「这一栏有没有写数」的答案。
 *  - `sectionLowerBound` = 这一组**真正**至少要选几样。必选组永远 >= 1。
 *                          **闸门与文案都读它**，所以两者结构上是同一个数。
 *
 * **地板不会造出新的死局**：`sectionMaxOption` 要么是 NO_CAP、要么是正整数，
 * 所以上限恒 >= 1 >= 地板，「选 1 样」永远是做得到的。会死的只有
 * 「后台把 `minOption` 配得比 `maxOption` 大」那种本来就配反了的组
 * （例如 min=5 / max=2：选到 2 样其余选项就置灰，而校验要 5）——
 * 那种组在这次改动**之前就已经死**，地板既没造出它也没让它更糟。
 *
 * 可选组一个字都没改：owner 同日裁定「可选组的下限不执行」，
 * 所以 `sectionLowerBound` 对它就是 `lowerBound` 原样转出。
 *
 * A **required** section can never be satisfied by picking nothing: its floor is 1.
 * `lowerBound` still answers "what did the backend configure" (and is unchanged);
 * `sectionLowerBound` answers "how many must the customer actually pick", which is
 * what every gate and every caption reads. The floor cannot deadlock a section —
 * the cap is either NO_CAP or a positive integer, so 1 is always reachable.
 *
 * ── 坏配置兜底：算出来做不到时，塌成「正好 N 样」（owner 2026-08-10 裁定）───
 *
 * 裁定原文：**「本质上是设置问题，这只是一个兜底让用户能过去的临时策略」**、
 * **「这是错误的配置，我给的是临时放行权利，不是让用户做的绝对正确的权利」**、
 * **「这种情况我只给一种放行方式，而不是绝对正确的方式」**。
 *
 * 读法：兜底**不是在猜店家的本意**，是在保证顾客能往下走。所以它只取一个做得到的数，
 * 不做「哪种解释更合理」的推断。真正的修复是去改设置，代码只负责别把人卡死、
 * 并且把坏配置记下来（`console.warn`，不写回任何地方）。
 *
 * 本仓此前**从来没有**这道兜底 —— `required` + `range` + `min=5 / max=2` 是个**活死局**：
 * 选到 2 样其余选项就全灰（上限），而闸门要 5 样（下限），顾客怎么点都加不进购物车，
 * 屏上还念着「Select at least 5」。现在它塌成「正好 2 样」，红字也跟着念「Select 2」。
 *
 * ⚠️ **上限 clamp 到「选项条数」只在兜底里做；下限是全局的**（§8.6，2026-08-10 修订）。
 *
 * **可选组**的选项是可以加份的（每一项带数量，顾客能按 `+`），所以
 * 「2 个选项、最多 5 样」是**做得到的**（3 份 A + 2 份 B），是正常配置：
 * `lo > hi` 不成立 → 根本进不了兜底 → 上限不 clamp。把上限 clamp 提到全局会把
 * 这种正常配置砍成「最多 2 样」，而店家的本意是「这两种酱料一共给你 5 份」。
 *
 * **下限反过来 —— 必选组的下限必须先 clamp。** 本仓的**必选组根本不能加份**：
 * `canIncreaseOption` 第一件事就是 `if (mod.type === 'required') return false`，
 * 而 `isRadioSelect` 让必选组连 +/- 控件都不画（两处都在 ./modOptionNumber）。
 * 于是「下限 8 / 上限 9 / 只有 2 个选项」在本仓是**真死锁**，而它 `lo > hi`
 * 不成立、进不了兜底 —— 所以下限那一半在第 ②½ 步就先降到选项条数。
 * 降下来永远安全：只会让配置更容易满足，绝拦不掉正常订单。
 *
 * ⚠️ **`exactly` 组是上面那条的例外：它的两头是同一个数，必须一起降**（§8.13）。
 * 只降下限会把那**一个**数劈成两个 ——「正好 3 样」+ 2 个选项变成「2 到 3 样」，
 * 而本仓的 `exactly` 闸门卡的是 `selectedNum !== sectionMaxOption`（`=== N`），
 * 读到的还是 3：**死胡同一点没修好**。一起降之后它塌成「正好 2 样」，`=== 2` 可满足。
 *
 * ⚠️ **而正因为 `exactly` 会连上限一起降，②½ 只能对「不能加份」的组做。**
 * 降上限**会**拦掉订单，§8.6 那句「clamp 下限永远安全」对可选组不再成立：
 * 可选组能加份，「可选 + 正好 3 样 + 只有 1 个选项」是**做得到的**（同一个酱料 3 份），
 * 照字面 clamp 会把一个能用的配置砍成「正好 1 样」。所以本仓的 ②½ 只跑必选组 ——
 * 判据 `cannotStackPortions` 与 `canIncreaseOption` 那条早退同源。
 *
 * ⚠️ **「必选组一个选项都没有」这一格谁都救不了。** 第 ②½ 步先把下限降到 0，
 * 再被必选地板抬回 **1**（§8.13 的 `lo = max(lo, 1)`）—— 而那一组里一样都没有，
 * 1 也拿不出来。数量规则到这里已经无话可说：真正接住这一格的是另一条裁定 ——
 * **那个商品在顾客端根本不展示**（见 utils/productDisplay.js），加购按钮也置灰。
 * 两条规则合起来才不漏。
 *
 * ── 认不出来的 `limitType` 一律当 `exactly`（owner 2026-08-10 裁定）─────────
 *
 * 本仓此前的行为是「不是 `exactly` 就走 `maxOption`」，于是**没写 `limitType`**
 * 的必选组：上限 NO_CAP、下限地板 1，而三条校验路的 `if / else if` 链
 * （`exactly` / `range` / `limit`）**没有 `else`** —— 那种组一条校验都不跑，
 * 一样都不选照样加进购物车，屏幕上却写着「Required」。
 * 现在它与 `exactly` 同一条路：说得出数就是那个数，说不出且必选就是 1。
 *
 * A section whose `limitType` is missing or misspelled is treated as `exactly`;
 * when the resolved bounds are impossible (`lo > hi`) they collapse to a single
 * reachable number N, so the gate and the caption can never disagree again.
 */

/** 不截断 / no usable limit is configured. */
export const NO_CAP = null

/**
 * 「恰好选 N 样」的**必选**组两个字段都说不出 N 时的 N。
 * 见文件头第二节：必选组这一档不许是 NO_CAP，否则整组永远判红、商品加不进单。
 * 可选组不吃这个兜底 —— 它压根不做校验，兜底只会把它弄窄。
 * The number a **required** `exactly` section falls back to when neither field
 * carries one. Optional sections do not use it.
 */
export const EXACTLY_DEFAULT_LIMIT = 1

/**
 * 必选组的地板：一样都不选**不允许**过（owner 2026-08-10 裁定，见文件头末节）。
 * 这个 `1` 只写在这一处 —— 闸门、文案、套餐子品的「还差几样」全从这里取。
 * The floor for a required section: picking nothing is never acceptable.
 */
export const REQUIRED_MIN_PICKS = 1

/**
 * 这一组是不是必选组。
 *
 * 判据与 `modSectionState.isSectionLocked`、以及三个渲染文件里那些
 * `type === 'required'` 的门逐字一致。本文件刻意不 import modSectionState ——
 * 那边 import 本文件，反过来引会成环；这里留一份同样的字面判据，
 * 两边一致这件事由 productDetailSectionLimit.spec.js 钉住。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
const isRequiredSection = (mod) => mod?.type === 'required'

/**
 * 「这一栏里有没有一个能用的数」—— 契约 v5 的唯一判据（见文件头 `parseUsable`）。
 * The one predicate behind every "does this field carry a usable quantity" question.
 *
 * `parseInt` 截断取整（`'1.5'` → 1），然后只有**正整数**算数：`0` / 负数 /
 * `NaN` / `±Infinity` 一律当"这一栏没写"。不是 number 也不是 string 的东西
 * （布尔、数组、对象、null、undefined）连解析都不解析 —— `parseInt(['5'])` 会
 * 从数组的字符串形式里读出 5，那不是「后台存了个数」，是形状本身坏了。
 *
 * `Number.isInteger` 在这里同时挡住 `NaN` 与 `±Infinity`：`parseInt` 的返回值
 * 只有这三类（整数 / NaN / ±Infinity），所以这一句就是契约里那条
 * 「n 不是有限数 or n <= 0 → null」。
 *
 * 上限（`sectionMaxOption`）与下限（`lowerBound`）问的是**同一个问题**，
 * 所以它们问的是同一个函数 —— 两把尺子是这类 bug 的来源，不是解决办法。
 *
 * @param {*} value 后台存进来的原值
 * @returns {Number|null} 能用的正整数，或 null（这一栏没写）
 */
const parseUsable = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  const n = parseInt(value)
  return (Number.isInteger(n) && n > 0) ? n : null
}

/**
 * 这一组的 `limitType` 归一化成三种之一：`'range'` / `'limit'` / `'exactly'`。
 *
 * **认不出来的值一律是 `'exactly'`**（没写 / `''` / `null` / 拼错的 `'Range'`）——
 * owner 2026-08-10 裁定。三条校验路与五处红字文案的 `if / else if` 链都问这个函数，
 * 所以「没有 `else` 就一条校验都不跑」那个洞不可能再长回来：这里没有第四条出路。
 *
 * The one normalisation of `limitType`; anything unrecognised is `exactly`.
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {'range'|'limit'|'exactly'}
 */
export const sectionLimitKind = (mod) => {
  if (mod?.limitType === 'range') return 'range'
  if (mod?.limitType === 'limit') return 'limit'
  return 'exactly'
}

/**
 * 这一组列出来几个选项 —— 坏配置兜底里那个 clamp 的上界（见文件头「坏配置兜底」）。
 *
 * 拿不到列表（跨仓体检那种只喂四个字段的 group、或数据还没到）时是 `Infinity`：
 * 「不知道有几个」只能是「不 clamp」，绝不能当成 0 —— 当成 0 会把一个本来
 * 只是配反了的必选组直接钉死在「正好 1 样」上，而它可能有 5 个选项。
 *
 * 数的是**列表长度**，不是「顾客看得见几个」：`unShow` 的选项在别处另有门
 * （见 utils/modSectionState 的 `isOptionHidden`），而这里要的是一个五仓喂同样
 * 输入就给同样答案的数，不是一个本仓自己解释出来的数。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Number}
 */
const optionListSize = (mod) => (Array.isArray(mod?.options) ? mod.options.length : Infinity)

/** 同一个坏配置只喊一次，别在每次点击 / 每次重绘上刷屏。 */
const warnedBrokenSections = new Set()

/**
 * 这一组**照配置算出来做不到**，喊一声。**只喊，不写回任何地方** ——
 * 真正的修复是去后台改设置（owner 2026-08-10：「本质上是设置问题」）。
 *
 * ⚠️ **别说「上限和下限写反了」**（§8.16①）。触发它的有两类，只有一类是写反：
 *
 *  - ③ 兜底：`下限 > 上限` —— 这一类确实是两个数写反了；
 *  - ②½：`下限 > 选项条数` —— **可能一个字都没写反**。「必选 + 完全没写
 *    `minOption` + 一个选项都没有」也会走到这里：地板给出 `下限 = 1`，而 `1 > 0`。
 *    那一组的问题是**没有选项**，不是「两个数配反了」。
 *
 * 所以措辞只说「算出来做不到」+ 报出实际的三个数，让看日志的人自己判断是哪一类。
 *
 * @param {Object} mod 配菜组
 * @param {Number} lo 调整之前的下限
 * @param {Number|null} hi 调整之前的上限（null = 没配上限）
 * @param {Number} n 调整之后那一头变成的数
 */
const warnBrokenSection = (mod, lo, hi, n) => {
  const key = `${mod?.name}|${mod?.limitType}|${mod?.minOption}|${mod?.maxOption}|${lo}|${hi}|${n}`
  if (warnedBrokenSections.has(key)) return
  warnedBrokenSections.add(key)
  const count = Array.isArray(mod?.options) ? mod.options.length : '未知'
  // eslint-disable-next-line no-console
  console.warn(
    `[sectionLimit] 配菜组「${mod?.name ?? '(未命名)'}」按配置算出来做不到：` +
    `至少 ${lo} 样 / 最多 ${hi === null ? '不限' : hi} 样 / 实际有 ${count} 个选项。` +
    `这一组按 ${n} 样放行。请到后台检查这一组的选项与数量设置。`
  )
}

/**
 * 这一组的上下限一起算出来 —— **§8 坏配置兜底规范的唯一实现，五仓逐字一致**。
 * The one implementation of the section's pick bounds (both ends at once).
 *
 * 三步照抄文件头那段伪码，一步都不许挪位置：
 *  ① 按 `limitType` 分派（认不出来的当 `exactly`）；
 *  ② 必选组的地板恒为 1（不读任何字段）；
 *  ③ **只在**算出来做不到（`lo > hi`）时兜底，塌成「正好 N 样」。
 *
 * 第 ③ 步的 clamp **只在这里**做。提到全局会把「2 个选项、最多 5 样」这种
 * 正常配置（选项可以加份）砍成「最多 2 样」——见文件头那段 ⚠️。
 *
 * @param {Object} mod 配菜组 / modification section
 * @param {Boolean} [isRequired] 按不按必选组算；默认按 `mod.type` 判
 * @returns {{ lower: Number|null, upper: Number|null }}
 */
export const sectionBounds = (mod, isRequired = isRequiredSection(mod)) => {
  const kind = sectionLimitKind(mod)
  let lower
  let upper

  // ① 按 limitType 分派
  if (kind === 'range') {
    // 两头各自成立：这是唯一一种「下限与上限是两个独立的数」的组
    lower = parseUsable(mod?.minOption)
    upper = parseUsable(mod?.maxOption)
  } else if (kind === 'limit') {
    // `limit` 不读 minOption —— 那一栏在这种组里可能是被镜像写进去的上限
    lower = null
    upper = parseUsable(mod?.maxOption)
  } else {
    // `exactly`，以及**任何认不出来的值**：min 的数量为主、其次 max，
    // 两个都说不出数时必选组兜底 1、可选组不封顶（owner 2026-08-08 第四次裁定）。
    const n = parseUsable(mod?.minOption) ?? parseUsable(mod?.maxOption) ??
      (isRequired ? EXACTLY_DEFAULT_LIMIT : NO_CAP)
    upper = n
    // ⚠️ §8.7①：`lo = hi = n` 不能逐字照抄 —— 可选组两个键都读不出时 `n` 是
    // **NO_CAP（不限）**，而「不限」在下限那一侧根本不是一个量，那里的答案是
    // 「没配下限」。本仓这两个哨兵碰巧都是 `null`，所以这一行照写就是对的；
    // 但别把它们当成同一件事：哪天 NO_CAP 换了值（后端那一侧就是 PHP_INT_MAX），
    // 逐字照抄会让下限变成「至少 9223372036854775807 样」= 谁都过不去。
    lower = n
  }

  // ② 必选组的地板：一样都不选**不允许**过，一个字段都不读
  if (isRequired && lower === null) lower = REQUIRED_MIN_PICKS

  // ②½ 下限不能超过**实际选得到的**数量（§8.6，§8.13 修正）
  //
  // 本仓的必选组**不能给同一个选项加份**：`canIncreaseOption` 第一件事就是
  // `if (mod.type === 'required') return false`，而 `isRadioSelect` 让必选组
  // 连 +/- 控件都不画（见 ./modOptionNumber）。所以必选组能凑出来的最大份数
  // **就是选项条数**，「下限 8 / 上限 9 / 只有 2 个选项」是**真死锁** ——
  // 而它 `lo > hi` 不成立，第 ③ 步的兜底根本接不住它。这一步就是为它加的。
  //
  // 只 clamp **下限**，不 clamp 上限：下限降下来只会让配置更容易满足，永远安全；
  // 而上限超过选项条数在**可选组**里是正常配置（可选组能加份，「2 种酱料一共 5 份」）。
  // 非 `exactly` 组的上限**任何一步都不 clamp**（§8.9(b) 把兜底里那一步也删了）。
  //
  // ⚠️ **`exactly` 组是例外：它的上下限是同一个数，必须一起降**（§8.13）。
  // 只降下限会把那**一个**数劈成两个：「正好 3 样」+ 2 个选项 → `lo=2, hi=3`，
  // 语义从「正好 3 样」变成「2 到 3 样」；而本仓所有 `exactly` 闸门卡的是
  // `selectedNum !== sectionMaxOption`（`=== N`），读到的还是 3 —— **死胡同没修好**。
  // owner 原话是「最终答案肯定是 min=max，最大等于 list 的大小」：`exactly`
  // 本来就是 `min=max`，clamp 之后理应还是。
  //
  // ⚠️ 这一格**跨仓体检看不见**（`optionCount` 不是矩阵的轴），只能靠本仓的测试
  // （productDetailBrokenLimitFallback.spec.js 的 CLAMP_TABLE / EXACTLY_CLAMP_TABLE）。
  //
  // ⚠️ **只对「不能加份」的组做**（本仓 = 必选组）。§8.6 的安全性论证原话是
  // 「clamp 下限永远安全 —— 它只会让配置更容易满足」，而 §8.13 让 `exactly` 的
  // **上限**也跟着降 —— 降上限**会**拦掉订单，那句论证对可选组不再成立：
  // 可选组能加份，所以「可选 + 正好 3 样 + 只有 1 个选项」是**做得到的**
  // （同一个酱料点 3 份），照字面 clamp 会把它砍成「正好 1 样」= 弄坏一个能用的配置。
  // 这正是 §8.2 点名要避开的那类事故，只是 §8.13 没有回头重看那一条。
  // 详见交回说明里「§8.13 还不对的地方」。
  const optionCount = optionListSize(mod)
  const isExactlyLike = kind === 'exactly'
  // 本仓「能不能加份」与「是不是必选」是同一件事：`canIncreaseOption` 第一件事
  // 就是 `if (mod.type === 'required') return false`（见 ./modOptionNumber）。
  // 哪天那条早退没了，这里要跟着改 —— 它是这一步成立的全部理由。
  const cannotStackPortions = isRequired
  if (cannotStackPortions && lower !== null && lower > optionCount) {
    warnBrokenSection(mod, lower, upper, optionCount)
    lower = optionCount
    // 地板抬回来（0 个选项的必选组会先落到 0）。抬回来的 1 在「一个选项都没有」
    // 时仍然做不到，那一格由「该商品不展示」接住（见 utils/productDisplay.js）；
    // 数量规则到这里已经无话可说。
    // 写成显式的 `< 1` 而不是 `Math.max(lower, 1)`：这一句**只在 `选项条数 === 0`
    // 那一格才活**（`选项条数 >= 1` 时 `lower` 已经 >= 1，取较大者恒等于它自己）。
    // 显式判断能让读者一眼看出它服务的是哪一格（§8.16②）。
    if (isRequired && lower < REQUIRED_MIN_PICKS) lower = REQUIRED_MIN_PICKS
    // `exactly` 的两头是同一个数 —— 降完、抬完之后**一次**对齐（§8.13）。
    // ⚠️ 别照 §8.13 的伪码写成两句（降一次、抬一次各写一句）：那两句会**互相遮盖**，
    // 删掉任何一句行为都不变，于是变异测试两句都杀不掉 —— 两个假绿。
    if (isExactlyLike) upper = lower
  }

  // ③ 兜底：只有「两头都有数、而且下限比上限还大」才进来。整段只做一件事 ——
  // **`lo = hi`**（塌成「正好 hi 样」）。
  //
  // ⚠️ **§8.1 原文那三个算子这里一个都没有，是删掉的，不是漏了**（§8.9(b)）：
  //   - `N = min(lo, hi)`：守卫已保证 `lower > upper` → 恒等于 `upper`（§8.7②）；
  //   - `N = min(N, 选项条数)`：**必选组**走过 ②½ 之后 `lower <= max(选项条数, 1)`，
  //     于是 `upper < lower <= 选项条数` → 恒等于 `N`；
  //     **可选组**不走 ②½（它能加份，见 §8.15），对它 clamp 上限正是 §8.2 点名
  //     不许做的事 —— 「可选 / 下限 5 / 上限 2 / 只有 1 个选项」clamp 到 1 会把一个
  //     能靠加份满足的配置无故收窄。所以这一步对两种组**都不该有**；
  //   - `N = max(N, 1)`：`upper` 非 null 时恒 >= 1（`parseUsable` 只产正整数）→ 恒等于 `N`。
  // 三个都是杀不掉的死操作，留着只会让读者以为那里有一层防护。
  // **它们赖以成立的两条不变量由测试钉住**（见 productDetailBrokenLimitFallback.spec.js
  // 的「③ 的两条不变量」）：进到这一支时 `upper >= 1`，且必选组的 `upper < 选项条数`。
  // 哪天 ②½ 被改动、死代码重新变活，那两条会先红。
  //
  // ⚠️ **②½ 与 ③ 的先后对「算出来的两个数」不可观察**（§8.10 已作废）：两步都是
  // 「对 `lower` 取一次 min」（②½ 取 `min(lower, 选项条数)`、③ 取 `min(lower, upper)`），
  // 对同一个变量取 min 可交换；`exactly` 组因 `lower === upper` 两条路都进不了 ③。
  //
  // 实测（把两块整体对调跑全套件）：**1192 条里只红了 1 条，而且是
  // 「同一个坏配置只 warn 一次」那一条** —— 对调之后两块都会 warn，日志多一行。
  // 两头的数一格都不差。所以**别写「顺序」钉**：能红的只是一句日志噪音，
  // 把它当成「顺序被钉住了」就是钉代理指标。要钉就钉答案（见 CLAMP_TABLE）。
  if (lower !== null && upper !== null && lower > upper) {
    warnBrokenSection(mod, lower, upper, upper)
    lower = upper
  }

  return { lower, upper }
}

/**
 * 组的有效上限：一次最多能选几样。null = 这组没配上限，调用方一律按不限制处理。
 * The section's effective pick limit; null means "no usable limit configured".
 *
 * **必选**的 `exactly` 组（含认不出 `limitType` 的组）永远返回一个正整数，绝不返回
 * null；**可选**的那种组两个键都说不出数时返回 NO_CAP —— 见文件头第二节。
 * A **required** `exactly` section always resolves to a positive integer; an
 * **optional** one reports NO_CAP when neither field carries a number.
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Number|null}
 */
export const sectionMaxOption = (mod) => sectionBounds(mod).upper

/**
 * 后台在下限那一栏**配了**什么。null = 这一栏没配。
 * What the backend configured in the lower-bound field; null means "not configured".
 *
 * 这是 owner 2026-08-10 裁定 R4 落地的下限契约，五仓逐字一致。
 * 它**只回答「这一栏有没有写数、写的是几」**，不回答「顾客到底至少得选几样」——
 * 后者要看这组是不是必选，答案在 `sectionLowerBound`。
 *
 * ⚠️ **闸门与文案都不许直接调本函数。** 直接调就是把「后台没配」当成「不用选」，
 * 而必选组的地板是 1（owner 2026-08-10，见文件头末节）——
 * 那正是这次修掉的洞：`required range` 没配 `minOption` 时，勾一下再取消就能
 * 带着 0 个选择加进购物车，而组头还念着「Select between 1 and 3 (Required)」。
 * 要拦人 / 要念数，请调 `sectionLowerBound`。
 *
 * ⚠️ **`exactly` 组不许调本函数。** 那种组的 `minOption` 不是下限，
 * **它就是上限**（owner 2026-08-08 裁定，见 `sectionBounds` 的第三支）。
 * 「恰好选 N 样」没有「下限」这个概念，只有一个 N。谁在 `exactly` 支里调了
 * `lowerBound`，那是 bug。
 *
 * ⚠️ **本函数刻意对 `limitType` / `maxOption` / `type` 装聋**，一个分支都没有。
 * 这不是疏漏：跨仓体检（`scripts/check-limit-contract.js` 的 irrelevance 断言）
 * 要求六仓的 `lowerBound` 在这三栏上完全不敏感。§8 的分派（`limit` 不读
 * `minOption`、认不出的 `limitType` 当 `exactly`）与兜底全部长在**它上面那一层**
 * （`sectionBounds`），改本体会当场把体检打红。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Number|null} 后台配的下限，或 null（这一栏没配）
 */
export const lowerBound = (mod) => parseUsable(mod?.minOption)

/**
 * **按必选组算**这一组至少要选几样。永远是正整数。
 * The lower bound of a section resolved **as if it were required**.
 *
 * 「地板 1」是 owner 2026-08-10 裁定：**必选组不存在连 1 个都不选就能过去的。**
 * 那不是兜底猜测，那就是「必选」这两个字的意思。
 *
 * 与 `sectionLowerBound` 的差别只有一个：这里**不看 `mod.type`**，必选与否由
 * 调用方说了算。文案那一侧（./sectionDesc 的 `rangeCopy`）的必选与否是调用方
 * 显式传进来的开关，所以它用这一个；拿不准组类型的一律用 `sectionLowerBound`。
 *
 * ── 三件事挪走了，别照抄旧版本的理由（2026-08-10 §8）──────────────────
 *
 * 旧版在这里手写了「`exactly` 组返回常量 1、一个字段都不读」。§8 之后
 * **`exactly` 组的下限就是那个 N**（`lo = hi = n`）—— 这没有把任何强制"顺手上线"：
 * 那种组真正的闸门是 `selectedNum !== sectionMaxOption(mod)`，一直比任何下限都严，
 * 下限报 N 只是把同一件事说全了。整段分派与兜底现在都在 `sectionBounds` 里，
 * 这里只剩一次转调 —— 两个数出自同一次计算，结构上不可能再对不上。
 *
 * 旧版第 ② 条「不写 `Math.max(1, x)`，那是杀不掉的死操作」仍然成立，
 * 它现在长在 `sectionBounds` 第 ② 步（`lower === null` 才给 1）。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Number} >= 1 的整数
 */
export const requiredLowerBound = (mod) => sectionBounds(mod, true).lower

/**
 * 这一组**真正**至少要选几样 —— 六道下单闸门读的就是它。
 * How many the customer must actually pick; this is what every gate reads.
 *
 *   必选组 → >= 1，地板不可妥协
 *   可选组（含没写 `type` 的组）→ 后台配了多少就是多少（可能是 null）
 *
 * 可选组那一半是 owner 2026-08-10 同日的另一条裁定：**可选组的下限不执行**。
 * 本仓的事实与裁定一致 —— 三条校验路全都长在 `type === 'required'` 门里，
 * 六道闸门一道都不看可选组。所以这里对可选组**不加地板**：
 * 加了也没人执行，只会让文案再次宣布一条界面不执行的规则。
 *
 * 判据是「**是不是必选**」而不是 `type === 'optional'`：没写 `type` 的组同样
 * 不做校验，跟可选组走同一边（与 `isRequiredSection` 同源）。
 *
 * ⚠️ 它与 `sectionMaxOption` 现在出自**同一次** `sectionBounds` 计算，
 * 所以「屏幕念的数」与「闸门比的数」在坏配置上也不可能再打架 ——
 * 那正是 §8 兜底要收的东西。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Number|null} 至少要选的样数；可选组没配下限时是 null
 */
export const sectionLowerBound = (mod) => sectionBounds(mod).lower

/**
 * 一次只能选一个的组（有效上限为 1）。
 * 这种组里点另一个选项就是"换选"，不需要顾客先取消原来那个。
 *
 * @param {Object} mod 配菜组 / modification section
 * @returns {Boolean}
 */
export const sectionIsSingleSelect = (mod) => sectionMaxOption(mod) === 1
